import { Link } from 'react-router-dom'

import {
  Image,
  MemberStatus,
  ReportSlug,
} from '@tribeplatform/gql-client/types'
import {
  useAuthMember,
  useLeaderboardWithScores,
  useSpaceUtil,
} from '@tribeplatform/react-sdk/hooks'
import { Avatar } from '@tribeplatform/react-ui-kit/Avatar'

import { EmptyState } from './EmptyState'
import { LoadingState } from './LoadingState'

export const LeaderboardTab = ({ timeframe, membersCount, active }) => {
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions()
  const {
    data: membersWithScore,
    isLoading,
    isFetched,
  } = useLeaderboardWithScores({
    args: {
      includeAuthMember: true,
      input: {
        slug: ReportSlug.TOP_MEMBERS,
        timeFrame: timeframe,
        timeZone,
        limit: Math.ceil(membersCount * 1.5),
      },
    },
    fields: { member: { profilePicture: 'basic' }, score: 'all' },
    useQueryOptions: { enabled: active },
  })
  const { generateMemberPath } = useSpaceUtil()
  const { data: authMember } = useAuthMember()

  if (!isFetched || isLoading) return <LoadingState count={membersCount} />

  if (!membersWithScore?.length) return <EmptyState />

  /* Fetch more members than the limit, truncate to the limit, and add back 
    logged in member to the end of the list if they are truncated. */
  const activeMembers = [
    ...membersWithScore?.filter(
      memberScore => memberScore?.member?.status === MemberStatus.VERIFIED,
    ),
  ].splice(0, 10)
  if (!activeMembers?.find(member => member?.memberId === authMember?.id)) {
    const loggedInMember = membersWithScore?.find(
      member => member.memberId === authMember?.id,
    )
    if (loggedInMember) activeMembers.push(loggedInMember)
  }

  if (!activeMembers?.length) return <EmptyState />
  return (
    <div className="grid grid-cols-1 gap-1 -ml-2 -mr-2">
      {activeMembers.map(({ member, score }, index) => {
        if (!member?.id) return null
        return (
          <Link
            key={member.id}
            to={generateMemberPath(member)}
            className="group flex space-x-3 items-center p-2 rounded-md hover:bg-surface-100"
          >
            <span className="text-lg font-medium w-5 text-center flex-shrink-0 whitespace-nowrap">
              {member?.id === authMember?.id &&
              index === activeMembers.length - 1
                ? '-'
                : index + 1}
            </span>
            <Avatar
              size="md"
              src={(member?.profilePicture as Image)?.urls?.thumb}
              name={member?.name}
            />
            <div className="flex-grow max-w-full overflow-hidden">
              <div className="truncate text-basicSurface-900 flex items-center">
                {member?.name}
                {member?.id === authMember?.id && (
                  <span className="text-xs text-basicSurface-400 px-1.5">
                    (You)
                  </span>
                )}
              </div>
            </div>
            <div className="px-1 text-basicSurface-500">
              {score?.toLocaleString()}
            </div>
          </Link>
        )
      })}
    </div>
  )
}
